import React, { useState, useRef, useEffect } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/hooks/useLanguage';
import { translations } from '@/lib/translations';

interface AudioPlayerProps {
  audioUrl: string;
  title: string;
  coverImage: string;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
}

interface AudioPlayerState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isLoading: boolean;
  isMuted: boolean;
}

// Funzione per gestire URL audio (ora supporta solo file locali)
const processAudioUrl = (audioUrl: string): string => {
  // Controllo se audioUrl è definito
  if (!audioUrl || typeof audioUrl !== 'string') {
    console.error('AudioUrl non valido:', audioUrl);
    return '';
  }
  
  // Se è già un URL locale, ritornalo così com'è
  if (audioUrl.startsWith('/') || audioUrl.startsWith('./')) {
    return audioUrl;
  }
  
  // Se è un URL completo (http/https), ritornalo così com'è
  if (audioUrl.startsWith('http')) {
    return audioUrl;
  }
  
  // Altrimenti, assumiamo sia un percorso relativo e aggiungiamo il prefisso
  return `/${audioUrl}`;
};

// Funzione per formattare il tempo in mm:ss
const formatTime = (time: number): string => {
  if (isNaN(time)) return '0:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export function AudioPlayer({ 
  audioUrl, 
  title, 
  coverImage, 
  onPlay, 
  onPause, 
  onEnded 
}: AudioPlayerProps) {
  const { language } = useLanguage();
  const t = translations[language];
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  
  const [state, setState] = useState<AudioPlayerState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    isLoading: false,
    isMuted: false
  });

  // Processa l'URL audio
  const directAudioUrl = processAudioUrl(audioUrl);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setState(prev => ({
        ...prev,
        duration: audio.duration,
        isLoading: false
      }));
    };

    const handleTimeUpdate = () => {
      setState(prev => ({
        ...prev,
        currentTime: audio.currentTime
      }));
    };

    const handleEnded = () => {
      setState(prev => ({
        ...prev,
        isPlaying: false,
        currentTime: 0
      }));
      onEnded?.();
    };

    const handleLoadStart = () => {
      setState(prev => ({ ...prev, isLoading: true }));
    };

    const handleCanPlay = () => {
      setState(prev => ({ ...prev, isLoading: false }));
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [onEnded]);

  const togglePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (state.isPlaying) {
        audio.pause();
        setState(prev => ({ ...prev, isPlaying: false }));
        onPause?.();
      } else {
        await audio.play();
        setState(prev => ({ ...prev, isPlaying: true }));
        onPlay?.();
      }
    } catch (error) {
      console.error('Errore nella riproduzione audio:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    const progressBar = progressRef.current;
    if (!audio || !progressBar || !state.duration) return;

    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * state.duration;
    
    audio.currentTime = newTime;
    setState(prev => ({ ...prev, currentTime: newTime }));
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newVolume = parseFloat(e.target.value);
    audio.volume = newVolume;
    setState(prev => ({ 
      ...prev, 
      volume: newVolume,
      isMuted: newVolume === 0
    }));
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (state.isMuted) {
      audio.volume = state.volume;
      setState(prev => ({ ...prev, isMuted: false }));
    } else {
      audio.volume = 0;
      setState(prev => ({ ...prev, isMuted: true }));
    }
  };

  const progressPercentage = state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0;

  return (
    <div className="bg-gray-50 rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
      <audio
        ref={audioRef}
        src={directAudioUrl}
        preload="metadata"
      />
      
      {/* Header con copertina e titolo */}
      <div className="flex items-center gap-4 mb-6">
        <img
          src={coverImage}
          alt={title}
          className="w-16 h-16 rounded-lg object-cover shadow-md"
        />
        <div className="flex-1">
          <h3 className="font-semibold text-lg text-gray-900 line-clamp-2">
            {title}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {t.podcast.episode}
          </p>
        </div>
      </div>

      {/* Controlli principali */}
      <div className="flex items-center justify-center gap-4 mb-4">
        <button
          onClick={togglePlayPause}
          disabled={state.isLoading}
          className="relative w-20 h-20 rounded-full hover:scale-105 active:scale-95 transition-all duration-200 flex items-center justify-center group focus:outline-none"
          aria-label={state.isPlaying ? t.podcast.pause : t.podcast.play}
        >
          {/* Contenuto del pulsante */}
          <div className="relative z-10 flex items-center justify-center">
            {state.isLoading ? (
              <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" />
            ) : state.isPlaying ? (
              // Icona Pausa con Unicode
              <div className="text-white text-2xl font-bold leading-none flex items-center justify-center w-full h-full">
                <span className="block" style={{ fontSize: '24px', lineHeight: '1' }}>⏸️</span>
              </div>
            ) : (
              // Icona Play con Unicode
              <div className="text-white text-2xl font-bold leading-none flex items-center justify-center w-full h-full ml-1">
                <span className="block" style={{ fontSize: '24px', lineHeight: '1' }}>▶️</span>
              </div>
            )}
          </div>
        </button>
      </div>

      {/* Barra di progresso */}
      <div className="mb-4">
        <div
          ref={progressRef}
          className="w-full h-2 bg-gray-200 rounded-full cursor-pointer relative overflow-hidden"
          onClick={handleProgressClick}
          role="progressbar"
          aria-valuenow={progressPercentage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t.podcast.currentTime}
        >
          <div
            className="h-full bg-gray-700 rounded-full transition-all duration-100"
            style={{ width: `${progressPercentage}%` }}
          />
          {/* Indicatore di posizione */}
          <div
            className="absolute top-1/2 w-4 h-4 bg-gray-700 rounded-full shadow-md transform -translate-y-1/2 -translate-x-1/2 transition-all duration-100"
            style={{ left: `${progressPercentage}%` }}
          />
        </div>
        
        {/* Tempi */}
        <div className="flex justify-between text-sm text-gray-600 mt-2">
          <span>{formatTime(state.currentTime)}</span>
          <span>{formatTime(state.duration)}</span>
        </div>
      </div>

      {/* Controlli volume */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleMute}
          className="p-2"
          aria-label={state.isMuted ? t.podcast.unmute : t.podcast.mute}
        >
          {state.isMuted ? (
            <VolumeX className="w-4 h-4" />
          ) : (
            <Volume2 className="w-4 h-4" />
          )}
        </Button>
        
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={state.isMuted ? 0 : state.volume}
          onChange={handleVolumeChange}
          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          aria-label={t.podcast.volume}
        />
      </div>

      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #374151;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #374151;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </div>
  );
}